import { Context } from '../..';
import { ErrorSignalTypes } from '../../../enums/error-signal-types';
import { handleErrorToErrorPage } from '../functions';
import { QuestionAnswerProps } from '../../../models/overmind/questionnaire';
import { Questionnaire, QuestionnaireName } from '../../../models/questionnaire';
import * as base from '../base';
import { QuestionSetType } from '../../../enums/question-set-type';
import { AssessmentsWizardQuestionnaires } from '../../../pages/assessment-management/questionnaire/assessment-questionnaires.widget';

const CONTROLLER = `questionnaire`;

export async function postAssessmentQuestionnaireSlugs(context: Context, payload: AssessmentsWizardQuestionnaires): Promise<string | undefined> {
  const url = base.apiURL(CONTROLLER, context.state.companyVariables.slug!, payload.assessmentSlug);
  const request: base.IRequest = { url, method: 'POST', body: JSON.stringify(payload) };
  const response: base.IResponse<string> = await base.request(request);
  return response.data;
}

export async function getAssessmentQuestionnaireSlugs(
  context: Context,
  assessmentSlug: string
): Promise<
  | {
    questionnaireSlugs: string[];
  }
  | undefined
> {
  const url = base.apiURL(`${CONTROLLER}${base.params({
    companySlug: context.state.companyVariables.slug!,
    assessmentSlug: assessmentSlug
  })}`);
  const request: base.IRequest = { url, method: 'GET' };
  const response: base.IResponse<{
    questionnaireSlugs: string[];
  }> = await base.request(request);
  return response.data;
}

// @Jedidja
export async function getAssessmentQuestionnaires(context: Context): Promise<Questionnaire[] | undefined> {
  if (!context.state.activeAssessmentConfig?.assessmentSlug) {
    handleErrorToErrorPage(context, `/error/${ErrorSignalTypes.System}/404`, window.location.pathname);
    return undefined;
  }
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, context.state.activeAssessmentConfig?.assessmentSlug, `get-questionnaires`), authenticated: true };
  const response: base.IResponse<Questionnaire[]> = await base.request(request);
  return response.data;
}

export async function getQuestionSetSkillQuestionnaire(context: Context, requestParams: { questionSetType: QuestionSetType; skillSlug: string }): Promise<Questionnaire | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `get-question-set-skill-questionnaire`, requestParams.questionSetType.toString(), requestParams.skillSlug), authenticated: true };
  const response: base.IResponse<Questionnaire> = await base.request(request);
  return response.data;
}

export async function answerQuestionSetQuestion(context: Context, requestParams: { questionSetType: QuestionSetType; questionAnswers: QuestionAnswerProps }): Promise<number | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `answer-question-set-question`, requestParams.questionSetType.toString(), requestParams.questionAnswers.questionnaireSlug, requestParams.questionAnswers.answerId.toString()), authenticated: true, method: 'POST' };
  const response: base.IResponse<number> = await base.request(request);

  if (response.error) throw new Error(`Failed to save the answer to question`);

  return response.data;
}

// @Jedidja
export async function getQuestionnaires(context: Context, questionnaireSlug: string): Promise<Questionnaire | undefined> {
  if (!context.state.activeAssessmentConfig?.assessmentSlug) {
    handleErrorToErrorPage(context, `/error/${ErrorSignalTypes.System}/404`, window.location.pathname);
    return undefined;
  }
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `get-questionnaire`, context.state.activeAssessmentConfig?.assessmentSlug, questionnaireSlug), authenticated: true };
  const response: base.IResponse<Questionnaire> = await base.request(request);
  if (response.error) handleErrorToErrorPage(context, `/error/${ErrorSignalTypes.Api}/${response.error.status ?? 500}`, window.location.pathname);

  return response.data;
}

export async function getQuestionnaireNames(context: Context, companySlug: string): Promise<QuestionnaireName[] | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `get-questionnaires-names${base.params({ companySlug: companySlug })}`), authenticated: true };
  const response: base.IResponse<QuestionnaireName[]> = await base.request(request);

  return response.data;
}

// @Jedidja
export async function answerQuestion(context: Context, payload: QuestionAnswerProps): Promise<void> {
  if (!context.state.activeAssessmentConfig?.assessmentSlug) {
    handleErrorToErrorPage(context, `/error/${ErrorSignalTypes.System}/404`, window.location.pathname);
    return undefined;
  }
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `answer-question`, context.state.activeAssessmentConfig?.assessmentSlug, payload.questionnaireSlug, payload.answerId.toString()), authenticated: true, method: 'POST' };
  const response = await base.request(request);

  if (response.error) throw new Error(`Failed to save the answer to question`);
}

// @Jedidja
export async function completeQuestionnaireSection(context: Context, payload: QuestionAnswerProps): Promise<void> {
  if (!context.state.activeAssessmentConfig?.assessmentSlug) {
    handleErrorToErrorPage(context, `/error/${ErrorSignalTypes.System}/404`, window.location.pathname);
    return undefined;
  }
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `complete-questionnaire-skill`, context.state.activeAssessmentConfig?.assessmentSlug, payload.questionnaireSlug, payload.answerId.toString()), authenticated: true, method: 'POST' };
  await base.request(request);
}

// @Jedidja
export async function completeQuestionnaire(context: Context, questionnaireSlug: string): Promise<void> {
  if (!context.state.activeAssessmentConfig?.assessmentSlug) {
    handleErrorToErrorPage(context, `/error/${ErrorSignalTypes.System}/404`, window.location.pathname);
    return undefined;
  }
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `complete-questionnaire`, context.state.activeAssessmentConfig?.assessmentSlug, questionnaireSlug), authenticated: true, method: 'POST' };
  await base.request(request);
}

export async function completeQuestionSet(context: Context, payload: { questionSetType: QuestionSetType; questionnaireSlug: string; skillSlug: string }): Promise<number | undefined> {
  const url = base.apiURL(CONTROLLER, `complete-question-set`, payload.questionSetType.toString(), payload.questionnaireSlug, payload.skillSlug);
  const request: base.IRequest = { url, method: 'POST' };
  const response: base.IResponse<number> = await base.request(request);
  return response.data;
}
